import { useIntl } from 'react-intl';
import { useAdvertiserCampaignContext } from 'sonata-platform-components';
import { Input, Selector } from 'sonata-react-components';

import AdvertiserCampaignSkeleton from './AdvertiserCampaignSkeleton/AdvertiserCampaignSkeleton';
import AdvertiserLogo from './AdvertiserLogo/AdvertiserLogo';
import { AdvertiserCampaignSelectorStyled } from './advertiserCampaignSelector.style';
import NewAdvertiserInput from './NewAdvertiserInput/NewAdvertiserInput';

function AdvertiserCampaignSelector(): JSX.Element {
  const { formatMessage } = useIntl();

  const {
    getAdvertiserOptions,
    getMediaAgencyOptions,
    getMediaGroupOptions,
    handleAdvertiserChange,
    handleMediaAgencyChange,
    handleMediaGroupChange,
    handleNewAdvertiserClose,
    isLoading,
    mediaGroupsPermission,
    mediaInfoDictionary,
    mediaInfoSelected,
    mode,
    onMediaAdvertiserChange,
    onMediaInfoSelectedChange,
    refetch,
    showNewAdvertiser,
    updateMediaInfoDictionaries,
    user,
    userRoles,
    validationError
  } = useAdvertiserCampaignContext();

  const { advertiser, agency, group } = mediaInfoSelected || {};
  const isDisabled = mode === 'notEditable';
  const mediaAvertiserError = Boolean(validationError);

  if (isLoading) {
    return <AdvertiserCampaignSkeleton />;
  }

  return (
    <AdvertiserCampaignSelectorStyled className="advertiserCampaignSelector" hasMediaGroup={mediaGroupsPermission}>
      <h2 className="advertiserCampaignSelector__title">
        {formatMessage({
          defaultMessage: 'Media & Client Information',
          id: 'advertiserSelector.title'
        })}
      </h2>
      {mediaInfoSelected && (
        <div className="advertiserCampaignSelector__groupAgencyContainer">
          {mediaGroupsPermission && (
            <Selector
              className="advertiserCampaignSelector__groupSelector"
              description={
                !group?._id && mediaAvertiserError
                  ? formatMessage({
                      defaultMessage: 'Media Group is required',
                      id: 'advertiserSelector.mediaGroupRequired'
                    })
                  : undefined
              }
              isDisabled={isDisabled}
              label={formatMessage({
                defaultMessage: 'Media Group',
                id: 'advertiserSelector.mediaGroup'
              })}
              onChange={handleMediaGroupChange}
              options={getMediaGroupOptions()}
              required
              selectorStatus={!group?._id && mediaAvertiserError ? 'wrong' : 'default'}
              showSearch
              type="simple"
              value={[
                {
                  id: group?._id || '',
                  label: group?.name || ''
                }
              ]}
            />
          )}
          <Selector
            className="advertiserCampaignSelector__agencySelector"
            description={
              mediaAvertiserError
                ? formatMessage({
                    defaultMessage: 'Media Agency is required',
                    id: 'advertiserSelector.mediaAgencyRequired'
                  })
                : undefined
            }
            isDisabled={isDisabled}
            label={formatMessage({
              defaultMessage: 'Media Agency',
              id: 'advertiserSelector.mediaAgency'
            })}
            onChange={handleMediaAgencyChange}
            options={getMediaAgencyOptions()}
            required
            showSearch
            type="simple"
            value={[
              {
                id: agency?._id || '',
                label: agency?.name || ''
              }
            ]}
          />
        </div>
      )}
      {showNewAdvertiser ? (
        mediaInfoSelected && (
          <NewAdvertiserInput
            isDisabled={isDisabled}
            mediaInfoDictionary={mediaInfoDictionary || []}
            mediaInfoSelected={mediaInfoSelected}
            onClose={handleNewAdvertiserClose}
            onMediaAdvertiserChange={onMediaAdvertiserChange}
            onMediaInfoSelectedChange={onMediaInfoSelectedChange}
            updateMediaInfoDictionaries={updateMediaInfoDictionaries}
          />
        )
      ) : (
        <div className="advertiserCampaignSelector__advertiserContainer">
          <Selector
            className="advertiserCampaignSelector__advertiserSelector"
            description={
              !advertiser?._id && mediaAvertiserError
                ? formatMessage({
                    defaultMessage: 'Advertiser is required',
                    id: 'advertiserSelector.mediaAdvertiserRequired'
                  })
                : undefined
            }
            isDisabled={isDisabled}
            label={formatMessage({
              defaultMessage: 'Advertiser',
              id: 'advertiserSelector.mediaAdvertiser'
            })}
            onChange={handleAdvertiserChange}
            options={getAdvertiserOptions()}
            required
            selectorStatus={!advertiser?._id && mediaAvertiserError ? 'wrong' : 'default'}
            showSearch
            type="simple"
            value={[
              {
                id: advertiser?._id || '',
                label: advertiser?.name || ''
              }
            ]}
          />
          <div className="advertiserCampaignSelector__advertiserUrlContainer">
            <Input
              className="advertiserCampaignSelector__advertiserUrlInput"
              disabled
              id="advertiserUrlInput"
              label={formatMessage({
                defaultMessage: 'Advertiser URL',
                id: 'advertiserSelector.mediaAdvertiserUrl'
              })}
              name="advertiserSelector__advertiserUrlInput"
              notFloatingPlaceholder
              placeholder={advertiser?.url || 'N/A'}
              required
              shape="squareUI"
              type="text"
              value={advertiser?.url || 'N/A'}
            />
          </div>
        </div>
      )}
      {advertiser?._id && (
        <AdvertiserLogo
          disabled={isDisabled}
          mediaAdvertiser={advertiser}
          mediaInfoDictionary={mediaInfoDictionary || []}
          onMediaInfoDictionariesReload={refetch}
          user={user}
          userRoles={userRoles}
        />
      )}
    </AdvertiserCampaignSelectorStyled>
  );
}

export default AdvertiserCampaignSelector;
